import React, { useState } from 'react';
import { useAuth } from '@/shared/hooks';
import type { Tournament, User } from '@/shared/types';
import { HOME, type HomeState } from '@/shared/constants/home';
import { joinSession, leaveSession } from '@/shared/api';
import ShellHeader from '@/components/ShellHeader';
import '@/css/shared.css';
import '@/css/TournamentTable.css';
import TournamentTable from '@/components/shell/TournamentTable';
import JoinLeaveModal from '@/components/JoinLeaveModal';
import { type GameState } from '@/shared/constants/game';
import { useAppDispatch } from '@/store/hooks';
import { addNotification } from '@/store/slices/uiSlice';

type Props = {
  gameState: GameState;
  homeState: HomeState;
  setHomeState: (state: HomeState) => void;
  refetchUser: () => void;
  user: User | null;
};

function Sessions<T extends Tournament>({ gameState, homeState, setHomeState, refetchUser, user }: Props) {
  const [selectedSession, setSelectedSession] = useState<T | null>(null);
  const [sessionData, setSessionData] = useState<T[]>([]);
  const [showJoinLeaveModal, setShowJoinLeaveModal] = useState(false);
  const [joinedSessions, setJoinedSessions] = useState<Set<number>>(new Set());
  const [joiningId, setJoiningId] = useState<number | null>(null);
  const [leavingId, setLeavingId] = useState<number | null>(null);
  const { firebaseUser, loading } = useAuth();
  const dispatch = useAppDispatch();

  const handleJoin = async (session: T) => {
    if (loading || !firebaseUser) {
      return;
    }

    try {
      const idToken = await firebaseUser.getIdToken();

      setJoiningId(session.ID);
      await joinSession(gameState, session.ID, idToken);
  
      setJoinedSessions(prev => new Set(prev).add(session.ID));
      setSessionData(prev =>
        prev.map(t =>
          t.ID === session.ID
            ? { ...t, CurrentParticipants: t.CurrentParticipants + 1 }
            : t
        )
      );

      refetchUser();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to join tournament.';
      dispatch(addNotification({ message, type: 'error' }));
    } finally {
      setJoiningId(null);
    }
  };
  
  const handleLeave = async (session: T) => {
    if (loading || !firebaseUser) {
      return;
    }

    try {
      const idToken = await firebaseUser.getIdToken(); 

      setLeavingId(session.ID);
      await leaveSession(gameState, session.ID, idToken);
  
      setJoinedSessions(prev => {
        const updated = new Set(prev);
        updated.delete(session.ID);
        return updated;
      });      

      setSessionData(prev =>
        prev.map(t =>
          t.ID === session.ID
            ? { ...t, CurrentParticipants: t.CurrentParticipants - 1 }
            : t
        )
      );

      refetchUser();
    } catch (err) {
      console.error(err);
      alert('Failed to leave tournament.');
    } finally {
      setLeavingId(null);
    }
  };

  function openModal(){
   setShowJoinLeaveModal(true)
  }

  function accept(){
    if (!selectedSession) return;
    if (!selectedSession?.IsJoined) {
      handleJoin(selectedSession);
    } else {
      handleLeave(selectedSession);
    }
    closeModal()
  }

  function closeModal() {
    setShowJoinLeaveModal(false)
  }

  return (
    <div>
      <ShellHeader setHomeState={setHomeState} text={homeState === HOME.FC ? "Upcoming Tournaments" : "No Prize"}/>
      <TournamentTable
        user={user}
        gameState={gameState}
        openModal={openModal}
        sessionData={sessionData}
        setSessionData={setSessionData}
        setSelectedSession={setSelectedSession}
        joinedSessions={joinedSessions}
        joiningId={joiningId}
        leavingId={leavingId}
      />
      {showJoinLeaveModal && selectedSession && (
        <JoinLeaveModal 
          accept={accept} 
          closeModal={closeModal} 
          selectedSession={selectedSession}
        /> 
      )}
    </div>
  );
}

export default Sessions;
